/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
import React from "react";
// BASE components
import BASELoading from "../../../../../../../@BASE/core/BASELoading";
import BASESvgIcon from "../../../../../../../@BASE/core/BASESvgIcon";
// material ui components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
// custom components
import ComponentCard from "../components/Component";
import ComponentPreview from "../components/Preview";
import ComponentStepLabel from "../components/StepLabel";
import Instructions from "../components/Instructions";
import SubComponentCard from "../components/SubComponent";

export default function PEArgumentCounterArgument(props) {
  // deconstruct props
  const { data, dir, ins = [], course, comments = [] } = props;
  // set text size based on language direction
  const tl = `${dir === "rtl" ? "text-18" : "text-14"} ${course?.language?.acronym}`.trim();

  // if no data, return loading
  if (!data) {
    return <BASELoading />;
  }

  // filer comments by component_id
  const componentCommentsCount = (component) => {
    return comments.filter(
      (comment) =>
        comment.component === component && comment.internality !== true
    ).length;
  };

  return (
    <div>
      <style>
        {`
          .wysiwyg p {
            margin: 0px;
          }
        `}
      </style>

      <Instructions
        ins={ins}
        course={course}
        dir={dir}
        tl={tl}
        componentCommentsCount={componentCommentsCount}
      />

      <Typography className="text-16 font-bold p-16 pb-0">
        Page Content:
      </Typography>

      <ComponentCard
        title="Question"
        id="question"
        commentsCount={componentCommentsCount("question")}
      >
        {/* <ComponentStepLabel number="1" title="Prompt:" /> */}
        <Paper elevation={0} variant="outlined" square className="p-16 m-16">
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography
                className={`${tl} m-0 wysiwyg`}
                dir={dir}
                dangerouslySetInnerHTML={{ __html: data.prompt }}
              />
            </Grid>
          </Grid>
        </Paper>
      </ComponentCard>

      <ComponentCard
        title="NPC Video"
        id="npc"
        commentsCount={componentCommentsCount("npc")}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <ComponentStepLabel number="1" title="NPC Name:" />
            <Paper elevation={0} variant="outlined" square className="p-16 m-16">
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography className="text-14 m-0">
                    {data.npc.name}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ComponentStepLabel number="2" title="NPC Title:" />
            <Paper elevation={0} variant="outlined" square className="p-16 m-16">
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography className="text-14 m-0">
                    {data.npc.title}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <ComponentStepLabel number="3" title="NPC Transcript:" />
            {data.core &&
              data.core.map((segment, index) => (
                <SubComponentCard
                  key={segment.id}
                  title={"Segment " + (index + 1)}
                  id={segment.id}
                  commentsCount={componentCommentsCount(segment.id)}
                >
                  <Paper elevation={0} square className="p-16">
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4} lg={2}>
                        <Typography className="text-14 font-semibold m-0">
                          Video Segment Description:
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={8} lg={10}>
                        <Typography className="text-14 m-0 whitespace-pre-wrap">
                          {segment.description}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} lg={2}>
                        <Typography className="text-14 font-semibold m-0">
                          Narration:
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={8} lg={10}>
                        <Typography
                          className={`${tl} m-0 whitespace-pre-wrap`}
                          dir={dir}
                        >
                          {segment.text_tl}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4} lg={2}>
                        <Typography className="text-14 font-semibold m-0">
                          Segment Time
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={8} lg={10}>
                        <Typography className="text-14 m-0 whitespace-pre-wrap">
                          {segment.start_minute}:
                          {segment.start_second < 10 ? `0${segment.start_second}` : segment.start_second} -
                          {segment.end_minute}:
                          {segment.end_second < 10 ? `0${segment.end_second}`: segment.end_second}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </SubComponentCard>
              ))
            }
          </Grid>
        </Grid>
      </ComponentCard>

      <ComponentCard
        title="Feedback Response:"
        id="response"
        commentsCount={componentCommentsCount("response")}
      >
        {/* <ComponentStepLabel number="1" title="Prompt:" /> */}
        <Paper elevation={0} variant="outlined" square className="p-16 m-16">
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography
                className={`${tl} m-0 wysiwyg`}
                dir={dir}
                dangerouslySetInnerHTML={{ __html: data.response }}
              />
            </Grid>
          </Grid>
        </Paper>
      </ComponentCard>
    </div>
  );
}
